/**
 * Status Communication Tracker Component
 * Displays checkboxes for tracking WhatsApp and Email communications per status
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card, Checkbox, Space, Typography, Spin, message } from "antd";
import { Box } from "@mui/material";
import {
  getStatusCommunications as fetchStatusCommunications,
  updateStatusCommunication,
} from "../services/orderService";

const { Text } = Typography;

export const StatusCommunicationTracker = ({ orderId, currentStatus }) => {
  const [communications, setCommunications] = useState({});
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const loadCommunications = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchStatusCommunications(orderId);
      if (result.success && result.data) {
        setCommunications(result.data || {});
      }
    } catch (error) {
      console.error("Error loading communications:", error);
      message.error("Failed to load communication status");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Load communications on mount
  useEffect(() => {
    if (orderId) {
      loadCommunications();
    }
  }, [orderId, loadCommunications]);

  const handleCommunicationChange = async (status, type, checked) => {
    setUpdating(true);
    try {
      await updateStatusCommunication(orderId, status, {
        [type === "whatsapp" ? "whatsappSent" : "emailSent"]: checked,
      });
      setCommunications({
        ...communications,
        [status]: {
          ...(communications[status] || {}),
          [type === "whatsapp" ? "whatsappSent" : "emailSent"]: checked,
          [type === "whatsapp" ? "whatsappSentAt" : "emailSentAt"]: checked
            ? new Date().toISOString()
            : null,
        },
      });
      message.success("Communication status updated");
    } catch (error) {
      console.error("Error updating communication:", error);
      message.error("Failed to update communication status");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusCommunications = (status) => {
    return (
      communications[status] || {
        whatsappSent: false,
        emailSent: false,
      }
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleString("en-ZA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // All possible order statuses
  const allStatuses = [
    "pending",
    "booked",
    "in_progress",
    "arrived_at_storage",
    "in_transit_from_storage",
    "completed",
    "cancelled",
  ];

  const statusLabels = {
    pending: "Pending",
    booked: "Booked",
    in_progress: "In Progress",
    arrived_at_storage: "Arrived at Storage",
    in_transit_from_storage: "In Transit from Storage",
    completed: "Completed",
    cancelled: "Cancelled",
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <Spin size="large" />
      </Box>
    );
  }

  return (
    <Card title="Status Update Communications" style={{ marginTop: 16 }}>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {allStatuses.map((status) => {
          const comms = getStatusCommunications(status);
          const isCurrentStatus = status === currentStatus;

          return (
            <Card
              key={status}
              size="small"
              title={statusLabels[status]}
              style={{
                borderLeft: isCurrentStatus ? "4px solid #1890ff" : undefined,
              }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Box>
                  <Checkbox
                    checked={comms.whatsappSent}
                    onChange={(e) =>
                      handleCommunicationChange(
                        status,
                        "whatsapp",
                        e.target.checked
                      )
                    }
                    disabled={updating}
                  >
                    WhatsApp sent
                  </Checkbox>
                  {comms.whatsappSentAt && (
                    <Text
                      type="secondary"
                      style={{ marginLeft: 8, fontSize: 12 }}
                    >
                      {formatDate(comms.whatsappSentAt)}
                    </Text>
                  )}
                </Box>
                <Box>
                  <Checkbox
                    checked={comms.emailSent}
                    onChange={(e) =>
                      handleCommunicationChange(
                        status,
                        "email",
                        e.target.checked
                      )
                    }
                    disabled={updating}
                  >
                    Email sent
                  </Checkbox>
                  {comms.emailSentAt && (
                    <Text
                      type="secondary"
                      style={{ marginLeft: 8, fontSize: 12 }}
                    >
                      {formatDate(comms.emailSentAt)}
                    </Text>
                  )}
                </Box>
              </Space>
            </Card>
          );
        })}
      </Space>
    </Card>
  );
};
